import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Plus, X, Users, User, Search } from 'lucide-react';
import { apiClient, type UserGroup } from '../../api/client';

export interface ApprovalRuleEntry {
  type: 'user' | 'group';
  id: string;
  label: string;
}

export interface ApprovalRule {
  entries: ApprovalRuleEntry[];
}

interface Props {
  value: ApprovalRule[];
  onChange: (rules: ApprovalRule[]) => void;
}

interface OptionItem {
  type: 'user' | 'group';
  id: string;
  label: string;
  sublabel?: string;
}

export function ApprovalRulesEditor({ value, onChange }: Props) {
  const [allUsers, setAllUsers] = useState<OptionItem[]>([]);
  const [allGroups, setAllGroups] = useState<OptionItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded) return;
    Promise.all([
      apiClient.listTenantUsers().catch(() => ({ users: [] })),
      apiClient.listUserGroups().catch(() => []),
    ]).then(([userData, groups]) => {
      const users = ((userData as any).users || userData || []).map((u: any) => ({
        type: 'user' as const,
        id: u.user_id || u.id,
        label: u.given_name && u.family_name
          ? `${u.given_name} ${u.family_name}`
          : u.email || u.user_id || u.id,
        sublabel: u.email,
      }));
      setAllUsers(users);
      setAllGroups((groups || []).map((g: UserGroup) => ({
        type: 'group' as const,
        id: g.id,
        label: g.name,
        sublabel: `${g.member_count} member${g.member_count !== 1 ? 's' : ''}`,
      })));
      setLoaded(true);
    });
  }, [loaded]);

  const addRule = () => {
    onChange([...value, { entries: [] }]);
  };

  const removeRule = (ruleIdx: number) => {
    onChange(value.filter((_, i) => i !== ruleIdx));
  };

  const addEntryToRule = (ruleIdx: number, entry: ApprovalRuleEntry) => {
    const updated = value.map((rule, i) => {
      if (i !== ruleIdx) return rule;
      if (rule.entries.some(e => e.type === entry.type && e.id === entry.id)) return rule;
      return { entries: [...rule.entries, entry] };
    });
    onChange(updated);
  };

  const removeEntryFromRule = (ruleIdx: number, entryIdx: number) => {
    const updated = value.map((rule, i) => {
      if (i !== ruleIdx) return rule;
      return { entries: rule.entries.filter((_, j) => j !== entryIdx) };
    });
    onChange(updated.filter(r => r.entries.length > 0));
  };

  const allOptions: OptionItem[] = [
    ...allGroups.map(g => ({ ...g })),
    ...allUsers.map(u => ({ ...u })),
  ];

  return (
    <div className="space-y-3">
      {value.length === 0 && (
        <p className="text-xs text-muted-foreground italic">Anyone in the org can approve.</p>
      )}

      {value.map((rule, ruleIdx) => (
        <React.Fragment key={ruleIdx}>
          {ruleIdx > 0 && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="flex-1 border-t" />
              <span className="font-semibold uppercase tracking-wider">or</span>
              <div className="flex-1 border-t" />
            </div>
          )}
          <RuleRow
            rule={rule}
            ruleIdx={ruleIdx}
            allOptions={allOptions}
            onAddEntry={(entry) => addEntryToRule(ruleIdx, entry)}
            onRemoveEntry={(entryIdx) => removeEntryFromRule(ruleIdx, entryIdx)}
            onRemoveRule={() => removeRule(ruleIdx)}
          />
        </React.Fragment>
      ))}

      <Button variant="outline" size="sm" onClick={addRule} className="text-xs">
        <Plus className="w-3.5 h-3.5 mr-1" />
        {value.length === 0 ? 'Add approval requirement' : 'Add alternative rule (OR)'}
      </Button>
    </div>
  );
}

function RuleRow({
  rule,
  ruleIdx,
  allOptions,
  onAddEntry,
  onRemoveEntry,
  onRemoveRule,
}: {
  rule: ApprovalRule;
  ruleIdx: number;
  allOptions: OptionItem[];
  onAddEntry: (entry: ApprovalRuleEntry) => void;
  onRemoveEntry: (entryIdx: number) => void;
  onRemoveRule: () => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const existingIds = new Set(rule.entries.map(e => `${e.type}:${e.id}`));
  const filtered = allOptions.filter(
    o => !existingIds.has(`${o.type}:${o.id}`) &&
      (o.label.toLowerCase().includes(query.toLowerCase()) ||
       (o.sublabel || '').toLowerCase().includes(query.toLowerCase()))
  );

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  return (
    <div className="rounded-lg border p-3 space-y-2 bg-card">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">
          {rule.entries.length > 1 ? 'All of these must approve' : 'Must approve'}
        </span>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground" onClick={onRemoveRule}>
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {rule.entries.map((entry, entryIdx) => (
          <Badge key={`${entry.type}-${entry.id}`} variant="secondary" className="flex items-center gap-1 pr-1">
            {entry.type === 'group' ? <Users className="w-3 h-3" /> : <User className="w-3 h-3" />}
            <span className="text-xs">{entry.label}</span>
            <button onClick={() => onRemoveEntry(entryIdx)} className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5">
              <X className="w-2.5 h-2.5" />
            </button>
          </Badge>
        ))}
        <button
          onClick={() => setSearchOpen(!searchOpen)}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>

      {searchOpen && (
        <div className="border rounded-md mt-1">
          <div className="flex items-center gap-2 px-2 py-1 border-b">
            <Search className="w-3.5 h-3.5 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search users or groups..."
              className="border-0 h-7 text-xs p-0 focus-visible:ring-0"
            />
          </div>
          <div className="max-h-36 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">No results</p>
            ) : filtered.slice(0, 15).map(opt => (
              <button
                key={`${opt.type}-${opt.id}`}
                onClick={() => {
                  onAddEntry({ type: opt.type, id: opt.id, label: opt.label });
                  setQuery('');
                }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors flex items-center gap-2"
              >
                {opt.type === 'group' ? <Users className="w-3.5 h-3.5 text-primary" /> : <User className="w-3.5 h-3.5 text-muted-foreground" />}
                <div className="min-w-0">
                  <div className="truncate">{opt.label}</div>
                  {opt.sublabel && opt.sublabel !== opt.label && (
                    <div className="text-muted-foreground truncate">{opt.sublabel}</div>
                  )}
                </div>
                {opt.type === 'group' && <Badge variant="outline" className="ml-auto text-[10px] px-1 py-0">Group</Badge>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
